import { CACHE, setEdgeCache } from "@/lib/http-cache";
import { ogImageMeta } from "@/lib/og";
import { useListingTracking } from "@/lib/use-listing-tracking";
import { createFileRoute, notFound, redirect, Link } from "@tanstack/react-router";
import { queryOptions, useQuery, useQueryClient, type QueryClient } from "@tanstack/react-query";
import { pageParam } from "@/lib/search-params";
import { stripSearchParams } from "@tanstack/react-router";
import { getTagBySlug, getPosts, decodeHtmlEntities, cleanTermName, primeTermId, featuredImage } from "@/lib/wp";
import { SITE, ROBOTS_INDEX, ROBOTS_NOINDEX_FOLLOW, ROBOTS_NOINDEX } from "@/lib/site";
import { ArticleCard, LEAD_CARD_SIZES } from "@/components/article-card";
import { deriveModernSrcSets } from "@/components/smart-image";
import { PageHeader } from "@/components/page-header";
import { TitleRule } from "@/components/title-rule";
import { AcademyFollowMore } from "@/components/academy/follow-more";

import { academyLinksForTerms } from "@/lib/academy/related-links";

import { ArticleGridSkeleton } from "@/components/article-grid-skeleton";
import { fastLoad, prefetchAdjacentPages } from "@/lib/fast-load";
import { COUNTRIES, resolveCountrySlug } from "@/lib/countries";
import type { SearchSchemaInput } from "@tanstack/react-router";

/**
 * Clean, human display name for a tag slug. Country tags (Hong Kong, New
 * Zealand, …) use their curated label so headings never fall back to a
 * lowercase slug while WordPress is still loading.
 */
function tagDisplayName(slug: string, wpName?: string | null): string {
  const country = COUNTRIES.find((c) => c.slug === slug);
  if (country) return country.name;
  const cleaned = wpName ? cleanTermName(wpName) : "";
  if (cleaned) return cleaned;
  return slug
    .replace(/-/g, " ")
    .replace(/\b\w/g, (ch) => ch.toUpperCase());
}



/* Punt 5: the tag term itself changes rarely, so it gets its own 60-minute
 * entry. Hydration and the adjacent-page prefetch reuse the term the server
 * already resolved instead of issuing a second `/tags` request. */
const TERM_TTL_MS = 60 * 60 * 1000;

const tagTermQuery = (slug: string) =>
  queryOptions({
    queryKey: ["wp-tag-term", slug],
    staleTime: TERM_TTL_MS,
    gcTime: 6 * 60 * 60 * 1000,
    queryFn: () => getTagBySlug(slug).catch(() => null),
  });

const tagQuery = (qc: QueryClient, slug: string, page: number) =>
  queryOptions({
    queryKey: ["tag", slug, page],
    queryFn: async () => {
      // Term and posts are fetched in parallel and independently: a hiccup on
      // the term lookup must never blank out a country/tag listing.
      const [term, posts] = await Promise.all([
        qc.ensureQueryData(tagTermQuery(slug)).catch(() => null),
        getPosts({ tagSlug: slug, page, perPage: 16 }).catch(() => null),
      ]);
      if (!term && !posts) return null;
      return {
        term,
        posts: posts ?? { items: [], meta: { total: 0, totalPages: 1 } },
      };
    },
  });



export const Route = createFileRoute("/tag/$slug")({
  // Country tags consolidate on the canonical /markets/<country> hub.
  beforeLoad: ({ params, search }) => {
    const country = resolveCountrySlug(params.slug);
    if (country) {
      throw redirect({ to: "/markets/$country", params: { country }, search: { page: search.page }, statusCode: 301 });
    }
  },
  validateSearch: (search: { page?: unknown } & SearchSchemaInput) => ({ page: pageParam(search.page) }),
  search: { middlewares: [stripSearchParams({ page: 1 })] },
  loaderDeps: ({ search }) => ({ page: search.page }),
  loader: async ({ params, deps, context }) => {
    const { data, cached } = await fastLoad(context.queryClient, tagQuery(context.queryClient, params.slug, deps.page), 2500);
    if (cached && !data) throw notFound();
    await setEdgeCache(data ? CACHE.listing : CACHE.degraded);
    return { initial: data, page: deps.page };
  },
  head: ({ loaderData, params }) => {
    if (!loaderData) return { meta: [{ title: "Tag not found" }, { name: "robots", content: ROBOTS_NOINDEX }] };
    const initial = loaderData.initial;
    const name = tagDisplayName(params.slug, initial?.term ? decodeHtmlEntities(initial.term.name) : null);
    const page = loaderData.page ?? 1;
    const totalPages = initial?.posts.meta.totalPages ?? 1;
    // Country tags duplicate the dedicated /markets/<country> landing page.
    // Consolidate all ranking signals on the market hub.
    const country = COUNTRIES.find((c) => c.slug === params.slug);
    const baseUrl = country
      ? `${SITE.url}/markets/${country.slug}`
      : `${SITE.url}/tag/${params.slug}`;
    const pageUrl = (n: number) => (n > 1 ? `${baseUrl}?page=${n}` : baseUrl);
    const canonical = pageUrl(page);
    const heading = country ? `${name} Retail News` : name;
    const title = page > 1 ? `${heading} - Page ${page} - ${SITE.name}` : `${heading} - ${SITE.name}`;

    const description = country
      ? `Latest retail, e-commerce and consumer news from ${name}, curated by ${SITE.name}.`
      : `Browse every story tagged ${name} on ${SITE.name}: reporting, analysis and market signals from across Asian retail and e-commerce.`;
    const collectionLd = {
      "@context": "https://schema.org",
      "@type": "CollectionPage",
      name: title,
      description,
      url: canonical,
      inLanguage: "en",
      isPartOf: { "@type": "WebSite", name: SITE.name, url: SITE.url },
      ...(country ? { about: { "@type": "Place", name } } : {}),
    };
    const links: Array<Record<string, string>> = [{ rel: "canonical", href: canonical }];

    // The first card image is the mobile LCP element. It is rendered eagerly
    // with fetchpriority=high; this preload mirrors the AVIF candidate set of
    // that exact <picture> so the preload scanner starts it during head
    // parsing without ever downloading a second copy. Only one image is
    // preloaded, and nothing is emitted when the lead card has no image.
    const leadPost = initial?.posts.items?.[0];
    const leadImg = leadPost ? featuredImage(leadPost) : null;
    if (leadImg?.src) {
      const modern = deriveModernSrcSets(leadImg.srcSet || undefined);
      if (modern) {
        links.push({
          rel: "preload",
          as: "image",
          type: "image/avif",
          href: `${leadImg.src}${leadImg.src.includes("?") ? "&" : "?"}format=avif`,
          imageSrcSet: modern.avif,
          imageSizes: LEAD_CARD_SIZES,
          fetchPriority: "high",
        });
      } else {
        links.push({ rel: "preload", as: "image", href: leadImg.src, fetchPriority: "high" });
      }
    }
    if (!country && page > 1) links.push({ rel: "prev", href: pageUrl(page - 1) });
    if (!country && page < totalPages) links.push({ rel: "next", href: pageUrl(page + 1) });
    return {
      meta: [
        ...ogImageMeta(),
        { title },
        { name: "description", content: description },
        {
          name: "robots",
          content: country || page > 1 ? ROBOTS_NOINDEX_FOLLOW : ROBOTS_INDEX,
        },
        { property: "og:site_name", content: SITE.name },
        { property: "og:locale", content: SITE.locale },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:url", content: canonical },
        { property: "og:type", content: "website" },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:site", content: SITE.twitter },
        { name: "twitter:title", content: title },
        { name: "twitter:description", content: description },
      ],

      links,
      scripts: [
        { type: "application/ld+json", children: JSON.stringify(collectionLd) },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Home", item: SITE.url },
              country
                ? { "@type": "ListItem", position: 2, name: "Markets", item: `${SITE.url}/markets` }
                : { "@type": "ListItem", position: 2, name: "Tags", item: `${SITE.url}/tags` },
              { "@type": "ListItem", position: 3, name: name, item: baseUrl },
            ],
          }),

        },
      ],
    };
  },
  component: TagPage,
  notFoundComponent: () => (
    <div className="container-editorial py-24 text-center">
      <h1 className="page-title">Tag not found</h1>
    </div>
  ),
});

function TagPage() {
  const { initial } = Route.useLoaderData();
  const { page } = Route.useSearch();
  const params = Route.useParams();
  const queryClient = useQueryClient();
  // Punt 5: reuse the server-resolved term instead of re-fetching it.
  if (initial?.term) {
    primeTermId("tag", params.slug, initial.term.id);
    if (queryClient.getQueryData(tagTermQuery(params.slug).queryKey) === undefined) {
      queryClient.setQueryData(tagTermQuery(params.slug).queryKey, initial.term);
    }
  }
  const { data } = useQuery({ ...tagQuery(queryClient, params.slug, page), initialData: initial ?? undefined });
  prefetchAdjacentPages(queryClient, (n) => tagQuery(queryClient, params.slug, n), page, data?.posts.meta.totalPages ?? 1);
  const term = data?.term;
  const posts = data?.posts;
  const name = tagDisplayName(params.slug, term ? decodeHtmlEntities(term.name) : null);
  const isCountry = COUNTRIES.some((c) => c.slug === params.slug);


  useListingTracking({
    listType: "tag",
    listId: params.slug,
    listName: name,
    page,
    resultCount: posts?.meta.total ?? null,
  });

  return (
    <>
      <PageHeader
        eyebrow={isCountry ? "Country desk" : "Tag"}
        title={isCountry ? `${name} Retail News` : name}
        
        description={
          decodeHtmlEntities(term?.description || "") ||
          (isCountry
            ? `Retail, e-commerce and consumer news from ${name}: reporting, analysis and market signals from ${SITE.name}.`
            : `Every story tagged ${name} on ${SITE.name}: reporting, analysis and market signals from across Asian retail and e-commerce.`)
        }
      />

      {!posts ? (
        <ArticleGridSkeleton />
      ) : (
        <section className="container-editorial py-10">
          <h2 className="sr-only">{isCountry ? `Latest ${name} stories` : `Stories tagged ${name}`}</h2>
          <p className="mb-4 text-xs uppercase tracking-wide text-ink-muted font-sans">
            {posts.meta.total.toLocaleString()} {posts.meta.total === 1 ? "story" : "stories"} in this topic
          </p>
          <TitleRule className="mb-8" />

          <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
            {posts.items.map((p: import("@/lib/wp").WPPost, i: number) => (
              <ArticleCard key={p.id} post={p} variant="lead" headingLevel={3} priority={i === 0} />
            ))}
          </div>
        </section>
      )}

      <div className="container-editorial">
        <AcademyFollowMore
          items={academyLinksForTerms([{ slug: params.slug, name }], 4)}
          heading="Background from the Retail Academy"
          intro="Evergreen guides that explain the mechanics behind this topic."
        />
      </div>


      <nav aria-label="Pagination" className="container-editorial flex items-center justify-between pb-16 hairline-t pt-4">
        {page > 1 ? (
          <Link to="/tag/$slug" params={{ slug: params.slug }} search={{ page: page - 1 }} className="inline-flex h-10 items-center border border-hairline px-4 text-xs font-semibold uppercase tracking-wide hover:border-brand hover:text-brand font-sans">← Newer</Link>
        ) : <span />}
        <span className="text-xs uppercase tracking-wide text-ink-muted font-sans">Page {page} of {posts?.meta.totalPages ?? 1}</span>
        {page < (posts?.meta.totalPages ?? 1) ? (
          <Link to="/tag/$slug" params={{ slug: params.slug }} search={{ page: page + 1 }} className="inline-flex h-10 items-center border border-hairline px-4 text-xs font-semibold uppercase tracking-wide hover:border-brand hover:text-brand font-sans">Older →</Link>
        ) : <span />}
      </nav>
    </>
  );
}
